import {
  DragLayerMonitorProps,
  DropOptions,
  Tree,
} from '@minoru/react-dnd-treeview';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { styled } from '@mui/material';
import { DocItem } from 'src/@types/docs';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useSnackbar } from 'src/components/snackbar';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useLocales } from 'src/locales';
import { getPositionForIndex } from 'src/sections/@dashboard/board/shared/utils';
import { useUpdateDocMutation } from '../useUpdateDocMutation';
import { CustomDragPreview } from './CustomDragPreview';
import { CustomNode } from './CustomNode';
import { TreeItem } from './types';
import {
  checkDocPermission,
  getAllParentIds,
  sortByOrder,
  useNavFilteredTreeItems,
} from './utils';

type Props = {
  isFromNav?: boolean;
  docs: DocItem[];
};

export const DocsTreeView = ({ isFromNav, docs }: Props) => {
  const { updateDoc, isUpdateDocLoading } = useUpdateDocMutation();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const { id: docId } = useParams();
  const { isSmallMobile } = useBreakpoints();

  const { treeItems, initialOpen } = useMemo(() => {
    const parentsWithChildren = new Set<string>();
    docs.forEach((d) => d.parent && parentsWithChildren.add(d.parent));

    let treeItems: TreeItem[] = docs
      .map<TreeItem>((doc) => ({
        id: doc._id,
        text: doc.title,
        parent: doc.parent || 'root',
        droppable: !doc.fixed && checkDocPermission(doc, user?._id),
        data: {
          order: doc.order!,
          createdAt: doc.createdAt,
          hasChildren: parentsWithChildren.has(doc._id),
          icon: doc.icon,
        },
      }))
      .sort(sortByOrder);

    if (isFromNav) {
      treeItems = useNavFilteredTreeItems(treeItems);
    }

    const initialOpen = docId ? getAllParentIds(treeItems, docId) : [];

    return { treeItems, initialOpen };
  }, [docs, isFromNav, user, docId]);

  const handleTreeDrop = async (_newTree: TreeItem[], options: DropOptions) => {
    if (isUpdateDocLoading) return;
    const isRoot = options.dropTargetId === 'root';
    const targetIsDroppable = !!options.dropTarget?.droppable;

    if (!isRoot && !targetIsDroppable) {
      return enqueueSnackbar(
        translate('shared.snackbar.not_have_permission_to_move_the_document'),
        { variant: 'error' },
      );
    }

    if (options.dragSourceId === options.dropTargetId) {
      return enqueueSnackbar(
        translate('shared.snackbar.cannot_move_document_to_itself'),
        { variant: 'error' },
      );
    }

    const dropTargetSubDocs = docs.filter((doc) => {
      if (isRoot) {
        return !doc.parent || doc.parent === 'root';
      }
      return doc.parent === options.dropTargetId;
    });

    const orders = dropTargetSubDocs.map((doc) => doc.order!);
    const nextOrder = getPositionForIndex(orders, options.relativeIndex!);

    try {
      await updateDoc({
        _id: options.dragSourceId as string,
        parent: options.dropTargetId as string,
        order: nextOrder,
      });
      enqueueSnackbar(
        translate('shared.snackbar.document_moved_successfully'),
        {
          variant: 'success',
        },
      );
    } catch (err) {
      enqueueSnackbar(translate('shared.snackbar.error_moving_document'), {
        variant: 'error',
      });
    }
  };

  const renderNode = (node: TreeItem, { depth, isOpen, onToggle }: any) => (
    <div style={{ display: 'flex', paddingLeft: `${depth * 8}px` }}>
      <CustomNode
        node={node}
        depth={depth}
        isOpen={isOpen}
        onToggle={onToggle}
      />
    </div>
  );

  return (
    <Tree
      rootId="root"
      tree={treeItems}
      render={renderNode}
      sort={false}
      dropTargetOffset={5}
      initialOpen={initialOpen}
      dragPreviewRender={(monitorProps: DragLayerMonitorProps<any>) => (
        <CustomDragPreview monitorProps={monitorProps} tree={treeItems} />
      )}
      onDrop={handleTreeDrop}
      insertDroppableFirst={false}
      canDrag={(node) =>
        !!node?.droppable && !isSmallMobile && !isUpdateDocLoading
      }
      canDrop={(tree, { dropTargetId, dragSourceId, dropTarget }) => {
        if (dropTargetId === dragSourceId) return false;

        const targetAncestors = getAllParentIds(
          treeItems,
          dropTargetId as string,
        );
        if (targetAncestors.includes(dragSourceId as string)) return false;

        if (dropTargetId === 'root') return true;

        return !!dropTarget?.droppable;
      }}
      placeholderRender={(node, { depth }) => <Placeholder left={depth * 24} />}
      classes={{
        root: 'treeRoot',
        draggingSource: 'draggingSource',
        dropTarget: 'dropTarget',
        container: 'tree-ul',
        placeholder: 'placeholderContainer',
      }}
      listItemComponent="div"
    />
  );
};

const Placeholder = styled('div')<{ left: number }>(({ left, theme }) => ({
  backgroundColor: theme.palette.primary.main,
  height: '2px',
  position: 'absolute',
  right: '0',
  top: '0',
  left: `${left}px`,
  transform: 'translateY(-50%)',
}));
